import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, GlassButton, GlassInput, GlassSelect } from '../../components/ui/GlassComponents';
import { countWorkingDays } from '../../utils/leaveDayCount';

const RequestLeave = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState({});
  const [workingDays, setWorkingDays] = useState(null); // null until fetched; util defaults to Mon-Fri
  
  const [leaveRequest, setLeaveRequest] = useState({
    leaveType: '',
    startDate: '',
    endDate: '',
    halfDay: false,
    reason: '',
    documents: null
  });

  // Fetch leave types and balances
  useEffect(() => {
    const fetchLeaveData = async () => {
      setLoading(true);
      try {
        console.log('Axios baseURL:', axios.defaults.baseURL);
        console.log('Token available:', !!localStorage.getItem('token'));
        
        // Fetch leave types
        console.log('Fetching leave types from: /api/leaves/types');
        const typesResponse = await axios.get('/api/leaves/types');
        console.log('Leave types response:', typesResponse);
        setLeaveTypes(typesResponse.data || []); // API now returns leave types array directly
        
        // Fetch leave balances
        console.log('Fetching leave balances from: /api/leaves/balances');
        const balancesResponse = await axios.get('/api/leaves/balances');
        console.log('Leave balances response:', balancesResponse);
        // Server returns balances object directly, not wrapped in a 'balances' property
        setLeaveBalances(balancesResponse.data || {});

        // Fetch the team to pick up workingDays so day-counting respects
        // teams configured Mon-Sat (or otherwise non-default).
        try {
          const teamResponse = await axios.get('/api/teams/current');
          if (teamResponse.data && Array.isArray(teamResponse.data.workingDays)) {
            setWorkingDays(teamResponse.data.workingDays);
          }
        } catch (e) {
          // Soft failure — util defaults to Mon-Fri, matching the historical
          // hardcoded behaviour, so the page still works.
          console.warn('Could not fetch team workingDays; defaulting to Mon-Fri.');
        }
      } catch (error) {
        console.error('Overall error fetching leave data:', error);
        console.error('Error details:', error.response ? error.response.data : 'No response data');
        console.error('Error status:', error.response ? error.response.status : 'No status');
        toast.error('Failed to load leave information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeaveData();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setLeaveRequest({
        ...leaveRequest,
        documents: files[0]
      });
    } else if (type === 'checkbox') {
      setLeaveRequest({
        ...leaveRequest,
        [name]: checked
      });
    } else {
      setLeaveRequest({
        ...leaveRequest,
        [name]: value
      });
    }
  };

  const calculateDays = () => {
    return countWorkingDays({
      startDate: leaveRequest.startDate,
      endDate: leaveRequest.endDate,
      workingDays, // null falls back to Mon-Fri inside the util
      halfDay: leaveRequest.halfDay
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!leaveRequest.leaveType) {
      toast.error('Please select a leave type');
      return;
    }
    
    if (!leaveRequest.startDate) {
      toast.error('Please select a start date');
      return;
    }
    
    if (!leaveRequest.endDate) {
      toast.error('Please select an end date');
      return;
    }
    
    const days = calculateDays();
    if (days <= 0) {
      toast.error('Please select valid dates');
      return;
    }
    
    // Check if user has enough leave balance
    const balance = leaveBalances[leaveRequest.leaveType] || 0;
    
    // Handle balance as either a number or an object with remaining property
    const availableDays = typeof balance === 'object' && balance !== null && 'remaining' in balance
      ? balance.remaining
      : balance;
      
    console.log('Leave balance check:', { 
      leaveType: leaveRequest.leaveType,
      requestedDays: days,
      balance,
      availableDays
    });
    
    if (days > availableDays) {
      // Find leave type name for better error message
      const leaveType = leaveTypes.find(type => type.id === leaveRequest.leaveType);
      const typeName = leaveType ? leaveType.name : leaveRequest.leaveType;
      
      toast.error(`You don't have enough ${typeName} leave balance (${availableDays} days available)`);
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Create form data for file upload
      const formData = new FormData();
      formData.append('leaveType', leaveRequest.leaveType);
      formData.append('startDate', leaveRequest.startDate);
      formData.append('endDate', leaveRequest.endDate);
      formData.append('halfDay', leaveRequest.halfDay);
      formData.append('reason', leaveRequest.reason);
      
      if (leaveRequest.documents) {
        formData.append('documents', leaveRequest.documents);
      }
      
      await axios.post('/api/leaves/request', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Leave request submitted successfully');
      navigate('/dashboard/leave-history');
    } catch (error) {
      console.error('Error submitting leave request:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit leave request';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Request Leave</h1>
        
        {/* Leave Balances */}
        <GlassCard className="p-6 mb-8 animate-fade-in">
          <h2 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">Your Leave Balances</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {leaveBalances && Object.keys(leaveBalances).length > 0 ? (
              Object.entries(leaveBalances).map(([typeId, balance]) => {
                // Find the leave type name from the leaveTypes array
                const leaveType = leaveTypes.find(type => type.id === typeId);
                const typeName = leaveType ? leaveType.name : typeId;
                
                // Check if balance is an object with limit, used, remaining properties
                const isBalanceObject = typeof balance === 'object' && balance !== null && 'remaining' in balance;
                const displayValue = isBalanceObject ? balance.remaining : balance;
                
                return (
                  <div key={typeId} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{typeName}</h3>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{displayValue} days</p>
                    {isBalanceObject && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>Total: {balance.limit} days</span>
                        <span className="mx-2">|</span>
                        <span>
                          <Link 
                            to={`/dashboard/leave-history?type=${typeId}`} 
                            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            Used: {balance.used} days
                          </Link>
                        </span>
                        {balance.adjustment !== 0 && (
                          <>
                            <span className="mx-2">|</span>
                            <span className={balance.adjustment > 0 ? 'text-green-500' : 'text-red-500'}>
                              Adjustment: {balance.adjustment > 0 ? '+' : ''}{balance.adjustment} days
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="col-span-3 text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">No leave balances available. Please configure leave types in Team Settings first.</p>
              </div>
            )}
          </div>
        </GlassCard>
        
        {/* Leave Request Form */}
        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="space-y-6">
            <div>
              <label htmlFor="leaveType" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Leave Type
              </label>
              <select
                id="leaveType"
                name="leaveType"
                value={leaveRequest.leaveType}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              >
                <option value="">Select leave type</option>
                {leaveTypes && leaveTypes.length > 0 ? (
                  leaveTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))
                ) : (
                  <option disabled>No leave types available</option>
                )}
              </select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Start Date
                </label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={leaveRequest.startDate}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  End Date
                </label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={leaveRequest.endDate}
                  onChange={handleChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
            </div>
            
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="halfDay"
                  name="halfDay"
                  type="checkbox"
                  checked={leaveRequest.halfDay}
                  onChange={handleChange}
                  className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="halfDay" className="font-medium text-gray-700 dark:text-gray-300">Half day on last day</label>
                <p className="text-gray-500 dark:text-gray-400">Check this if you're taking a half day on the last day of your leave</p>
              </div>
            </div>
            
            <div>
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Reason (Optional)
              </label>
              <textarea
                id="reason"
                name="reason"
                rows={3}
                value={leaveRequest.reason}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Briefly describe the reason for your leave request"
              />
            </div>
            
            <div>
              <label htmlFor="documents" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Supporting Documents (Optional)
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md dark:border-gray-600">
                <div className="space-y-1 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="flex text-sm text-gray-600 dark:text-gray-400">
                    <label htmlFor="documents" className="relative cursor-pointer bg-white dark:bg-gray-700 rounded-md font-medium text-primary-600 dark:text-primary-400 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                      <span>Upload a file</span>
                      <input id="documents" name="documents" type="file" className="sr-only" onChange={handleChange} />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    PDF, PNG, JPG, GIF up to 10MB
                  </p>
                  {leaveRequest.documents && (
                    <p className="text-sm text-primary-600 dark:text-primary-400">
                      {leaveRequest.documents.name}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <p className="font-medium text-gray-700 dark:text-gray-300">
                  Total Days: <span className="text-primary-600 dark:text-primary-400">{calculateDays()}</span>
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : 'Submit Request'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestLeave;
